import { createSelector } from '@reduxjs/toolkit';
import { ctvApi } from 'redux/rootApiSlice';

import {
  AdformDeal,
  Constants,
  DeviceTypes,
  DSPAdvertiser,
  InsertionOrdersCampaign,
  MediaTypes,
  Subcategories,
} from 'modules/misc/miscApiTypes';

import {
  DSP_ADFORM,
  DSP_AMAZON,
  DSP_CRITEO,
  DSP_FREEWHEEL,
  DSP_VISTAR,
  DSP_XANDR,
} from '../../utils/constants';

export const dataApiSlice = ctvApi.injectEndpoints({
  endpoints: (build) => ({
    constants: build.query<Constants, null>({
      query: () => ({ method: 'GET', url: 'constants' }),
      providesTags: [{ type: 'ApiData', id: 'Constants' }],
    }),
    mediaTypes: build.query<MediaTypes, { dsp?: string | null }>({
      query: ({ dsp }) => {
        const isXandr = dsp && dsp === DSP_XANDR;
        const basePath = isXandr ? 'types/media' : `${dsp}/types/media`;

        return {
          method: 'GET',
          url: basePath,
        };
      },
      transformResponse: (
        response: { id: number; name: string }[] | string[],
        _meta,
        arg,
      ): MediaTypes => {
        const isDsp = arg?.dsp && arg.dsp != DSP_XANDR;

        if (isDsp) {
          return (response as string[]).map((name, index) => ({
            id: index + 1,
            name,
          }));
        }

        return response as { id: number; name: string }[];
      },
      providesTags: [{ type: 'ApiData', id: 'MediaTypes' }],
    }),
    deviceTypes: build.query<DeviceTypes, { dsp?: string | null }>({
      query: ({ dsp }) => {
        const isXandr = dsp && dsp === DSP_XANDR;
        const basePath = isXandr ? 'types/device' : `${dsp}/types/device`;

        return {
          method: 'GET',
          url: basePath,
        };
      },
      transformResponse: (
        response: { id: number; name: string }[] | string[],
        _meta,
        arg,
      ): DeviceTypes => {
        const isXandr = arg?.dsp && arg.dsp === DSP_XANDR;

        if (isXandr) {
          return response as { id: number; name: string }[];
        }

        return (response as string[]).map((name, index) => ({
          id: index + 1,
          name,
        }));
      },
      providesTags: [{ type: 'ApiData', id: 'DeviceTypes' }],
    }),
    audiences: build.query<string[], { advertiserId: string; dsp?: string | null }>({
      query: ({ advertiserId, dsp }) => {
        const isXandr = dsp && dsp === DSP_XANDR;
        const basePath = isXandr ? 'line-item/audience' : `${dsp}/line-item/audience`;

        return {
          method: 'GET',
          url: `${basePath}?advertiserId=${advertiserId}`,
        };
      },
      providesTags: [{ type: 'ApiData', id: 'Audiences' }],
    }),
    advertisers: build.query<DSPAdvertiser[], string>({
      query: (dsp) => ({
        method: 'GET',
        url: `${dsp}/advertiser/names`,
      }),
    }),
    creatives: build.query<string[], { advertiserId: string; dsp?: string | null }>({
      query: ({ advertiserId, dsp }) => {
        const isXandr = dsp && dsp === DSP_XANDR;
        const basePath = isXandr ? 'creative/names' : `${dsp}/creative/names`;

        return {
          method: 'GET',
          url: `${basePath}?advertiserId=${advertiserId}`,
        };
      },
      providesTags: [{ type: 'ApiData', id: 'Creatives' }],
    }),
    lineItems: build.query<string[], { advertiserId: string; dsp?: string | null }>({
      query: ({ advertiserId, dsp }) => {
        const isXandr = dsp && dsp === DSP_XANDR;
        const basePath = isXandr ? 'line-item/names' : `${dsp}/line-item/names`;

        return {
          method: 'GET',
          url: `${basePath}?advertiserId=${advertiserId}`,
        };
      },
      providesTags: [{ type: 'ApiData', id: 'LineItems' }],
    }),
    insertionOrders: build.query<
      InsertionOrdersCampaign[],
      { advertiserId: string; dsp?: string | null }
    >({
      query: ({ advertiserId, dsp }) => {
        const isXandr = dsp && dsp === DSP_XANDR;
        const basePath = isXandr ? 'insertion-order/compact' : `${dsp}/insertion-order/compact`;

        return {
          method: 'GET',
          url: `${basePath}?advertiserId=${advertiserId}`,
        };
      },
      providesTags: [{ type: 'ApiData', id: 'InsertionOrders' }],
    }),
    publishers: build.query<string[], { dsp?: string | null }>({
      query: ({ dsp }) => {
        const isXandr = dsp && dsp === DSP_XANDR;
        const basePath = isXandr ? 'publisher/names' : `${dsp}/publisher/names`;

        return {
          method: 'GET',
          url: `${basePath}`,
        };
      },
      providesTags: [{ type: 'ApiData', id: 'Publishers' }],
    }),
    subcategories: build.query<Subcategories, string>({
      query: (advertiserId) => ({
        method: 'POST',
        url: `pixel/subcategories/${advertiserId}`,
      }),
      providesTags: [{ type: 'ApiData', id: 'Subcategories' }],
    }),
    splits: build.query<string[], string>({
      query: (advertiserId) => ({ method: 'GET', url: `split/names?advertiserId=${advertiserId}` }),
      providesTags: [{ type: 'ApiData', id: 'Splits' }],
    }),
    countries: build.query<string[], { dsp: string | null }>({
      query: ({ dsp }) => {
        const isXandr = dsp && dsp === DSP_XANDR;

        return {
          method: 'GET',
          // TODO: noop is to be updated with endpoint after backend API is ready for Xandr
          url: isXandr ? 'noop' : `${dsp}/types/country`,
        };
      },
      providesTags: [{ type: 'ApiData', id: 'Countries' }],
    }),
    adformDeals: build.query<AdformDeal[], void>({
      query: () => ({
        method: 'GET',
        url: 'adform/deals',
      }),
      transformResponse: (response: AdformDeal[]): AdformDeal[] => {
        return response.map((deal) => ({
          ...deal,
          value: deal.dealId,
          label: deal.name,
        }));
      },
      providesTags: [{ type: 'ApiData', id: 'AdformDeals' }],
    }),
    publisherDiscoveryDeals: build.query<string[], string>({
      query: (search) => ({
        method: 'GET',
        url: `publisher/discovery/deals/unmapped?search=${encodeURIComponent(search)}`,
      }),
      keepUnusedDataFor: 0, // Disable caching - always fetch fresh results
    }),
    publisherDiscoverySites: build.query<string[], string>({
      query: (search) => ({
        method: 'GET',
        url: `publisher/discovery/sites/unmapped?search=${encodeURIComponent(search)}`,
      }),
      keepUnusedDataFor: 0, // Disable caching - always fetch fresh results
    }),
    publisherDiscoveryApps: build.query<string[], string>({
      query: (search) => ({
        method: 'GET',
        url: `publisher/discovery/apps/unmapped?search=${encodeURIComponent(search)}`,
      }),
      keepUnusedDataFor: 0, // Disable caching - always fetch fresh results
    }),
    citySearch: build.query<string[], { dsp: string; search: string }>({
      query: ({ dsp, search }) => ({
        method: 'GET',
        url: `${dsp}/types/city?query=${encodeURIComponent(search)}`,
      }),
      keepUnusedDataFor: 0, // Disable caching - always fetch fresh results
    }),
    regionSearch: build.query<string[], { dsp: string; search: string }>({
      query: ({ dsp, search }) => ({
        method: 'GET',
        url: `${dsp}/types/region?query=${encodeURIComponent(search)}`,
      }),
      keepUnusedDataFor: 0, // Disable caching - always fetch fresh results
    }),
    adformMediaTypes: build.query<string[], void>({
      query: () => ({
        method: 'GET',
        url: 'adform/types/media',
      }),
      providesTags: [{ type: 'ApiData', id: 'AdformMediaTypes' }],
    }),
  }),
});

export const {
  useConstantsQuery,
  useDeviceTypesQuery,
  useMediaTypesQuery,
  useAudiencesQuery,
  useAdvertisersQuery,
  useCreativesQuery,
  useInsertionOrdersQuery,
  useLineItemsQuery,
  usePublishersQuery,
  useSubcategoriesQuery,
  useSplitsQuery,
  useCountriesQuery,
  useAdformDealsQuery,
  usePublisherDiscoveryDealsQuery,
  usePublisherDiscoverySitesQuery,
  usePublisherDiscoveryAppsQuery,
  useCitySearchQuery,
  useRegionSearchQuery,
  useAdformMediaTypesQuery,
  usePrefetch: useApiDataSlicePrefetch,
} = dataApiSlice;

export const selectConstants = createSelector(
  dataApiSlice.endpoints.constants.select(null),
  (constantsResult) => constantsResult.data,
);

export const selectMetrics = createSelector(
  dataApiSlice.endpoints.constants.select(null),
  (constantsResult) => constantsResult.data?.Metrics,
);

export const selectMetricsForDsp = (dsp: string | null) =>
  createSelector(dataApiSlice.endpoints.constants.select(null), (constantsResult) => {
    const metricsMap = constantsResult.data?.Metrics;

    if (!metricsMap) return {};

    const result: typeof metricsMap = {};

    for (const [key, metric] of Object.entries(metricsMap)) {
      if (
        ((!dsp || dsp === DSP_XANDR) && metric.relatesToXandrDashboard) ||
        (dsp && dsp === DSP_VISTAR && metric.relatesToVistarDashboard) ||
        (dsp && dsp === DSP_CRITEO && metric.relatesToCriteoDashboard) ||
        (dsp && dsp === DSP_ADFORM && metric.relatesToAdformDashboard) ||
        (dsp && dsp === DSP_AMAZON && metric.relatesToAmazonDashboard) ||
        (dsp && dsp === DSP_FREEWHEEL && metric.relatesToFreewheelDashboard)
      ) {
        result[key] = metric;
      }
    }

    return result;
  });

export const selectTimeSlice = createSelector(
  dataApiSlice.endpoints.constants.select(null),
  (constantsResult) => constantsResult.data?.TimeSlice,
);

export const selectDimensions = createSelector(
  dataApiSlice.endpoints.constants.select(null),
  (constantsResult) => constantsResult.data?.Dimensions,
);

export const selectCadence = createSelector(
  dataApiSlice.endpoints.constants.select(null),
  (constantsResult) => constantsResult.data?.Cadence,
);
